import { reactive, Ref, ref, UnwrapRef, watch } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';
import useImgPre from '@/renderer/components/SpacePage/compositions/useImagePreview';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import ipc from '@/renderer/scripts/utils/ipc';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

type RelatedToMeTableData = {
  list: SMH.RelatedToMeFileItemData[];
  selectedList: SMH.RelatedToMeFileItemData[];
  selectedRowKeys: Array<Number | String>;
};
type RelatedToMePageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};

type Handler = {
  tableData: UnwrapRef<RelatedToMeTableData>;
  pageData: UnwrapRef<RelatedToMePageData>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  sort: UnwrapRef<Sort>;
  columns: Array<any>;
  methods: {
    pullRelatedToMeList: () => Promise<void>;
    autoImagePreView: (params: { e: WheelEvent }, rowHeight: number) => void;
    onRelatedToMePageSizeChange: (pageSize: number, pageInfo: any) => void;
    onRelatedToMeCurrentChange: (current: number, pageInfo: any) => void;
    refresh: () => void;
    silentRefresh: () => void;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
  };
};
export default function relatedToMeListHandler(): Handler {
  const columns = [
    {
      colKey: 'name',
      title: i18n_t('文件名'),
      width: 200,
      ellipsis: {
        disabled: true,
      },
    },
    {
      colKey: 'description',
      title: i18n_t('动态'),
      width: 250,
      ellipsis: {
        disabled: true,
      },
    },
    {
      colKey: 'modificationTime',
      title: 'title-modificationTime',
      titleDesc: i18n_t('创建时间'),
      width: 150, // 由于此栏承载了操作按钮组，因此宽度必须 > 150px
      sortType: 'all',
      sorter: true,
      fixed: 'right',
    },
  ];
  const tableData = reactive<RelatedToMeTableData>({
    list: [],
    selectedList: [],
    selectedRowKeys: [],
  });
  const pageData = reactive<RelatedToMePageData>({
    currPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
        value: 50,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
        value: 100,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
        value: 200,
      },
    ],
  });
  const loading = ref(false);
  const loadingOnHeader = ref(false);
  const useImagePreview = useImgPre();
  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('relatedToMe', {
    sortBy: 'modificationTime',
    descending: true,
  });
  const { methods: extraFileInfoMethods } = extraFileInfoHandler();
  const methods = {
    async autoImagePreView(params: { e: WheelEvent }, rowHeight: number) {
      useImagePreview.debouncedFetchData(params, rowHeight, tableData, tableData.list);
    },
    async pullRelatedToMeList(silentLoading = false) {
      const startTime = new Date().getTime();
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        orderBy: innerSort.value.orderBy,
        orderByType: innerSort.value.orderByType,
      };
      try {
        loading.value = !silentLoading;
        loadingOnHeader.value = silentLoading;
        const { data: listRes } = await smh.userAPI.getRelatedToMeDirectory(opt);
        pageData.total = listRes.totalNum;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, listRes.totalNum);
        if (listRes.totalNum > 0) {
          const list = listRes.contents.map((item, index) => {
            return {
              ...item,
              invalid: !item.visible || (item.spaceTag === 'group' && !item.isUserBelongSpace),
              // visible 为 false 一定是无效 isUserBelongSpace 为 false，如果 spaceTag= group，那也是无效的
              id: index,
            };
          });
          tableData.list = list;
          extraFileInfoMethods.getFileExtraRefresh(tableData, { multiSpaceId: true });
          useImagePreview.getImagePre(tableData, list, 0, 20, 20);
        }
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        reporter.report('list_cost_time', {
          page: 'RelatedToMe',
          cost: new Date().getTime() - startTime,
          count: pageData.pageSize,
        });
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    onRelatedToMePageSizeChange(pageSize: number, pageInfo: any) {
      pageData.pageSize = pageSize;
      methods.pullRelatedToMeList();
    },
    onRelatedToMeCurrentChange(current: number, pageInfo: any) {
      pageData.currPage = current;
      methods.pullRelatedToMeList();
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullRelatedToMeList();
    },
    async refresh() {
      tableData.list = [];
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      await methods.pullRelatedToMeList();
    },
    silentRefresh() {
      if (!loadingOnHeader.value && !loading.value) {
        methods.pullRelatedToMeList(true);
      }
    },
  };
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  return {
    columns,
    tableData,
    pageData,
    loading,
    loadingOnHeader,
    sort: tableSort,
    methods,
  };
}
